/**
 * TranscriptionPanel — how much of the archive can be searched.
 *
 * Shown to teachers and admins only. Transcription needs the platform's AI
 * credit to be active; when it is not, this says so plainly instead of leaving
 * lectures quietly untranscribed.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, Loader2 } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Request failed');
  return body;
};

export const TranscriptionPanel = ({ tenantId }) => {
  const qc = useQueryClient();
  const status = useQuery({
    queryKey: ['transcription-status', tenantId],
    queryFn: () => api(`/school/tenants/${tenantId}/recordings/transcription-status`),
    retry: false,
  });

  const run = useMutation({
    mutationFn: () => api(`/school/tenants/${tenantId}/recordings/transcribe-backfill?limit=3`,
      { method: 'POST' }),
    onSuccess: (d) => {
      const done = (d.results || []).filter((r) => r.transcribed).length;
      toast[done ? 'success' : 'error'](
        done ? `Transcribed ${done} of ${d.attempted} — ${d.remaining} still waiting`
             : 'Nothing could be transcribed. Check the AI credit below.');
      qc.invalidateQueries({ queryKey: ['transcription-status', tenantId] });
    },
    onError: (e) => toast.error(e.message),
  });

  const d = status.data;
  if (status.isError || !d) return null;

  return (
    <Card className="p-4 mb-4" data-testid="transcription-panel">
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="h-4 w-4 text-indigo-600" />
        <p className="text-sm font-medium">Searchable lectures</p>
        <span className="text-xs text-slate-600 dark:text-slate-400 ml-auto"
              data-testid="transcription-counts">
          {d.done} transcribed · {d.waiting} waiting
          {d.not_held_here > 0 && ` · ${d.not_held_here} kept off-site`}
        </span>
      </div>
      {!d.transcriber_ready && (
        <p className="text-xs text-amber-700 dark:text-amber-400 mt-2"
           data-testid="transcriber-not-ready">
          {d.transcriber_reason
            || 'Transcription is unavailable, so new lectures cannot be searched yet.'}
        </p>
      )}
      {d.waiting > 0 && (
        <Button size="sm" variant="outline" className="mt-2"
                onClick={() => run.mutate()} disabled={run.isPending}
                data-testid="transcribe-backfill-btn">
          {run.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
          Transcribe the next three
        </Button>
      )}
      <p className="text-[11px] text-slate-500 mt-2">
        The rest are picked up automatically overnight.
      </p>
    </Card>
  );
};

export default TranscriptionPanel;
